<script lang="ts">
  import { onMount } from 'svelte';
  import { getAppConfig } from '../../common/config/app-config';
  import { isMobileWidth, sorterBasedOnAnotherArray } from '../../common/util';
  import ExtraSnackBar from '../common/components/ExtraSnackBar.svelte';
  import Icon from '../common/components/Icon.svelte';
  import Text from '../common/components/Text.svelte';
  import { HOME_MAINTENANCE_CONTENT_SLOT_ORDER } from '../common/lazy-components/driver/common-mapper';
  import LazyComponentLoader from '../common/lazy-components/driver/LazyComponentLoader.svelte';
  import { getCMSComponentsFromHybrisAsyncForHomeMaintenance } from '../common/services/hybris';
  import HMAtcDialog from './components/HMAtcDialog.svelte';
  import HomeMaintenanceServiceDialog from './components/HomeMaintenanceServiceDialog.svelte';
  import { openHomeMaintenancePopup } from './store/home_maintenance_store';

  let homeMaintenanceContentSlotInfo: string;
  let componentData;
  const { extraThemeResourcePath, pageId } = getAppConfig();
  // const bannerUrl = getStr('extra.home.service.sticky.banner.url');
  let showBanner = true;
  const isResponsive = isMobileWidth();

  $: {
    if (typeof document !== 'undefined') {
      const header = document.querySelector('.c_global-header');
      if (header) {
        if (!showBanner) {
          header.classList.add('hm-banner-closed');
        } else {
          header.classList.remove('hm-banner-closed');
        }
      }
    }
  }
  $: contentSlotToComponentUidsArray =
    homeMaintenanceContentSlotInfo &&
    (homeMaintenanceContentSlotInfo
      .substring(1, homeMaintenanceContentSlotInfo.length - 2)
      .split('], ')
      .map(s => s.replace(/=\[/g, '='))
      .map(s => s.split('='))
      .map(([a, b]) => [a, b])
      .filter(([slotName]) => HOME_MAINTENANCE_CONTENT_SLOT_ORDER.includes(slotName))
      .map(([slotName, compsString]) => [slotName, compsString.split(', ')])
      .sort(sorterBasedOnAnotherArray(HOME_MAINTENANCE_CONTENT_SLOT_ORDER)) as [string, string[]][]);

  $: slotNameToGroupedUidsArray =
    contentSlotToComponentUidsArray &&
    contentSlotToComponentUidsArray.length > 0 &&
    groupSlots(contentSlotToComponentUidsArray, {});

  $: componentUids =
    slotNameToGroupedUidsArray && slotNameToGroupedUidsArray.map(([, componentUids]) => componentUids).flat();
  $: if (componentUids && componentUids.length > 0) {
    getCMSComponentsFromHybrisAsyncForHomeMaintenance(componentUids, true).then(data => (componentData = data));
  }

  $: componentDataMap =
    componentData &&
    componentData.component &&
    componentData.component
      .map(c => {
        return { [c.uid]: c };
      })
      .reduce((prev, curr) => Object.assign(prev, curr), {});

  function groupSlots(sortedSlots: [string, string[]][], slotGroups): [string, string[]][] {
    const output = [];
    const processedCombineIds = [];
    sortedSlots.forEach(sortedSlot => {
      const [slotname] = sortedSlot;
      if (slotname in slotGroups) {
        const combineId = slotGroups[slotname];
        if (processedCombineIds.indexOf(combineId) === -1) {
          // combine the slots
          const slotNamesWithCurrentCombineId = Object.keys(slotGroups).filter(x => slotGroups[x] === combineId);
          const combinedUids = sortedSlots
            .filter(x => slotNamesWithCurrentCombineId.indexOf(x[0]) > -1)
            .map(x => x[1])
            .flat();
          const combinedSlot = [slotname, combinedUids];
          output.push(combinedSlot);
          processedCombineIds.push(combineId);
        }
      } else {
        output.push(sortedSlot);
      }
    });
    return output;
  }

  onMount(() => {
    homeMaintenanceContentSlotInfo = window.ACC?.config?.contentSlotInfo || null;

    if (pageId === 'extraHomeMaintenancePage' && typeof document !== 'undefined') {
      const applySmartbannerClass = () => {
        const smartbanner = document.querySelector('.js_smartbanner');
        if (smartbanner) {
          smartbanner.classList.add('hm-smartbanner');
          return true;
        }
        return false;
      };

      if (!applySmartbannerClass()) {
        const observer = new MutationObserver(() => {
          if (applySmartbannerClass()) {
            observer.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }
  });
  // function handleBannerClick() {
  //   window.open(bannerUrl, '_blank');
  // }
  function closeBanner(event) {
    event.stopPropagation(); // Prevent opening the link
    showBanner = false;
  }
</script>

<section class="home-maintenance-page-container">
  {#if showBanner}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="home-maintenance-page-sticky-banner"
      style="background: #F28B2E url('{extraThemeResourcePath}/images/homeservices/sticky-header-bg.png') no-repeat right;"
    >
      <div class="content">
        <img src="{extraThemeResourcePath}/images/safeguard-hm-new.png" class="" alt="Safeguard plan" />
        <span>
          <Text key="extra.home.service.sticky.banner.text" />
        </span>
      </div>
      {#if !isResponsive}
        <div on:click={closeBanner}>
          <Icon type="font" glyph="close" desktopSize={30} />
        </div>
      {/if}
    </div>
  {/if}
  {#if slotNameToGroupedUidsArray?.length > 0 && componentDataMap}
    {#each slotNameToGroupedUidsArray as [slotName, componentUids]}
      <LazyComponentLoader {slotName} {componentUids} componentsData={componentUids.map(c => componentDataMap[c])} />
    {/each}
  {/if}
  <section class="home-maintenance-dialog-wrapper">
    <HomeMaintenanceServiceDialog open={$openHomeMaintenancePopup} />
  </section>
  <section class="home-maintenance-add-to-cart-dialog-wrapper">
    <HMAtcDialog />
  </section>
  <ExtraSnackBar />
</section>

<style lang="scss">
  @import '../common/styles/colors.new.scss';

  .home-maintenance-page-container {
    display: flex;
    flex-direction: column;
    gap: 25px;
    @include desktop-max-width {
      gap: 14px;
    }

    .home-maintenance-page-sticky-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 15px;
      color: $primary-white;

      width: 100%;
      position: fixed;
      top: 0;
      z-index: 100;
      padding: 14px 0;
      left: 0;
      max-height: 70px;
      top: -1px;

      @include responsive {
        padding: 8px 0;
      }

      .content {
        display: flex;
        align-items: center;
        gap: 25px;
        @include responsive {
          gap: 10px;
        }

        img {
          @include responsive {
            width: 18px;
          }
        }
        span {
          font-weight: 500;
          font-size: 24px;
          line-height: 26px;
          color: $primary-white;
          @include responsive {
            font-size: 8px;
            line-height: 12px;
          }
        }
      }
    }

    :global {
      /* FIRST banner full-bleed */
      .slot-wrapper:first-child {
        margin: 0 -58px;
        background: linear-gradient(to bottom, rgba(234, 246, 249, 0.75) 0%, rgba(255, 255, 255, 1) 100%);
        @include desktop-max-width {
          margin: 0;
        }
        margin-bottom: -25px;
      }

      /* SECOND banner floating */
      [data-slotname='HomeMaintanenceVipTopBannerSlot'] {
        position: relative;
        margin-bottom: -249px;
        z-index: 5;
        padding: 0 50px;
        background: none !important;
        margin-top: 30px;
        @include desktop-max-width {
          margin: 0;
          padding: 0 3px;
          margin-top: 17px;
        }
      }
      [data-slotname='HomeMaintanenceTopBannerSlot'] {
        position: relative;
        margin-bottom: -18px;
        background: none !important;
      }
      [data-slotname='HomeMaintanenceBottomContentSlot'] {
        margin-bottom: -64px;
        @include desktop-max-width {
          margin-bottom: 0;
        }
      }

      .paragraph-component-container {
        padding: 0 15px;
        max-width: 1320px;
        margin: 0 auto;
      }
    }
  }
  :global(.c_global-header) {
    margin-top: 68px;
    @include desktop-max-width {
      margin-top: 30px;
    }
  }

  :global(.c_global-header.minimal) {
    position: relative;
  }

  :global(.c_global-header.hm-banner-closed) {
    margin-top: 0px;
    position: sticky;
  }
  @include desktop-max-width {
    :global(.hm-smartbanner) {
      top: 30px;
    }
  }
</style>
